import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../services/api';

interface UseTokenRefreshConfig {
    refreshInterval: number; // milliseconds between refresh attempts
    isActive: boolean;       // only refresh if user is active (not in warning state)
    onRefreshError?: () => void; // callback when refresh fails with 401/403
}

interface UseTokenRefreshReturn {
    isRefreshing: boolean;
    lastRefresh: Date | null;
    refreshError: string | null;
    manualRefresh: () => Promise<void>;
}

export const useTokenRefresh = ({
    refreshInterval = 15 * 60 * 1000, // 15 minutes default
    isActive,
    onRefreshError
}: UseTokenRefreshConfig): UseTokenRefreshReturn => {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
    const [refreshError, setRefreshError] = useState<string | null>(null);

    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Refresh the token
    const refreshToken = useCallback(async () => {
        // Don't refresh if already refreshing or if user is not active
        if (isRefreshing || !isActive) {
            return;
        }

        // Double-check that we still have a token before refreshing
        const currentToken = localStorage.getItem('auth_token');
        if (!currentToken) {
            console.log('[TokenRefresh] No token found, skipping refresh');
            return;
        }

        console.log('[TokenRefresh] Starting token refresh at', new Date().toLocaleTimeString());
        setIsRefreshing(true);
        setRefreshError(null);

        try {
            await apiClient.refreshToken();
            console.log('[TokenRefresh] Token refreshed successfully at', new Date().toLocaleTimeString());
            setLastRefresh(new Date());
        } catch (error: any) {
            const errorMessage = error?.message || 'Failed to refresh token';
            setRefreshError(errorMessage);
            console.error('[TokenRefresh] Token refresh error:', error);

            // If we get 401 or 403, the token is invalid - trigger logout
            if (error?.status === 401 || error?.status === 403) {
                console.warn('[TokenRefresh] Token refresh failed with auth error (401/403). Triggering logout.');
                // Clear any pending retries
                if (retryTimeoutRef.current) {
                    clearTimeout(retryTimeoutRef.current);
                    retryTimeoutRef.current = null;
                }
                onRefreshError?.();
                return;
            }

            // For network errors, retry once after 30 seconds (only if still active)
            if (isActive && (error?.status >= 500 || error?.message?.includes('Network'))) {
                retryTimeoutRef.current = setTimeout(() => {
                    refreshToken();
                }, 30000); // 30 seconds
            }
        } finally {
            setIsRefreshing(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive, onRefreshError]); // isRefreshing intentionally excluded to prevent infinite re-creation of callback

    // Manual refresh function
    const manualRefresh = useCallback(async () => {
        await refreshToken();
    }, [refreshToken]);

    // Set up automatic refresh interval
    useEffect(() => {
        // Only set up interval if user is active
        if (!isActive) {
            // Clear interval if user becomes inactive
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        // Don't refresh on mount, only set up interval
        // Set up interval for periodic refresh
        intervalRef.current = setInterval(() => {
            refreshToken();
        }, refreshInterval);

        // Cleanup on unmount or when dependencies change
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
        };
    }, [refreshInterval, isActive, refreshToken]);

    return {
        isRefreshing,
        lastRefresh,
        refreshError,
        manualRefresh
    };
};
